import React, { useEffect, useState } from 'react';
import { BarChart3, Loader2, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

const Delta = ({ cur, prev, invert = false, suffix = '' }) => {
  if (!prev) return <span className="text-xs text-gray-400">—</span>;
  const d = ((cur - prev) / prev) * 100;
  const good = invert ? d < 0 : d > 0;
  const Icon = Math.abs(d) < 0.5 ? Minus : good ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${Math.abs(d) < 0.5 ? 'text-gray-400' : good ? 'text-green-700' : 'text-red-600'}`}><Icon size={12} /> {d > 0 ? '+' : ''}{d.toFixed(0)} %{suffix}</span>;
};

const Kpi = ({ label, value, cur, prev, invert, testId }) => (
  <div className="bg-gray-50 rounded-lg p-3" data-testid={testId}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-xl font-bold text-gray-900">{value}</p>
    <Delta cur={cur} prev={prev} invert={invert} />
  </div>
);

const Table = ({ rows, keyName, label, testId }) => (
  <div className="bg-white border border-gray-100 rounded-lg overflow-hidden" data-testid={testId}>
    <table className="w-full text-xs">
      <thead className="bg-gray-50 text-gray-500"><tr><th className="text-left px-3 py-2 font-medium">{label}</th><th className="text-right px-2 py-2 font-medium">Clics</th><th className="text-right px-2 py-2 font-medium">Impr.</th><th className="text-right px-2 py-2 font-medium">CTR</th><th className="text-right px-3 py-2 font-medium">Pos.</th></tr></thead>
      <tbody className="divide-y divide-gray-100">
        {rows.slice(0, 15).map((r) => (
          <tr key={r[keyName]} className="hover:bg-orange-50/40">
            <td className="px-3 py-1.5 max-w-[280px] truncate text-gray-800" title={r[keyName]}>{keyName === 'page' ? r.page.replace(/^https?:\/\/[^/]+/, '') || '/' : r.query}</td>
            <td className="px-2 py-1.5 text-right font-medium">{r.clicks}</td><td className="px-2 py-1.5 text-right text-gray-600">{r.impressions}</td><td className="px-2 py-1.5 text-right text-gray-600">{r.ctr} %</td><td className="px-3 py-1.5 text-right text-gray-600">{r.position}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400">Aucune donnée sur la période</td></tr>}
      </tbody>
    </table>
  </div>
);

const Spark = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.clicks));
  return (
    <div className="flex items-end gap-[2px] h-12" data-testid="gsc-sparkline" title="Clics par jour">
      {daily.map((d) => <div key={d.date} className="flex-1 bg-[#1e3a5f]/80 rounded-t-sm min-w-[2px]" style={{ height: `${Math.max(4, (d.clicks / max) * 100)}%` }} title={`${d.date} : ${d.clicks} clics · ${d.impressions} impressions`} />)}
    </div>
  );
};

// Admin › Paramètres : performances Google Search Console (Search Analytics) — clics, impressions, CTR, position, top pages / requêtes
export const SeoPerformancePanel = () => {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (refresh = false) => {
    setBusy(true);
    try { setData(await adminAPI.gscPerformance(days, refresh)); } catch (e) { setData({ configured: false, error: e.response?.data?.detail || 'Erreur' }); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.snapshot;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-6" data-testid="seo-performance-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-gray-900 inline-flex items-center gap-2"><BarChart3 size={18} className="text-[#1e3a5f]" /> Performances Google Search Console</h2>
          <p className="text-xs text-gray-500">Clics, impressions, CTR et position moyenne de {data?.site || 'la propriété'} · données Google (décalage ~2 jours), rafraîchies toutes les 6 h.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="gsc-days" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
            <option value={7}>7 jours</option><option value={28}>28 jours</option><option value={90}>90 jours</option>
          </select>
          <Button type="button" size="sm" variant="outline" onClick={() => load(true)} disabled={busy || !data?.configured} data-testid="gsc-refresh">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</Button>
        </div>
      </div>
      {!data && <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>}
      {data && !data.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="gsc-not-configured">
          <p className="font-medium inline-flex items-center gap-1"><AlertTriangle size={14} /> Search Console non configurée</p>
          <p className="text-xs mt-1">Renseignez le JSON du compte de service Google et la propriété (ex. <code>sc-domain:otopieces.com</code>) dans la carte « Soumission aux moteurs de recherche » ci-dessus, puis ajoutez l'e-mail du compte de service comme utilisateur de la propriété dans Search Console.</p>
          {data.error && <p className="text-xs mt-1 text-red-700">{data.error}</p>}
        </div>
      )}
      {data?.configured && data.error && <p className="text-xs text-red-700 mb-2" data-testid="gsc-error"><AlertTriangle size={12} className="inline mr-1" />{data.error}{s ? ' — affichage de la dernière récupération.' : ''}</p>}
      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Kpi label="Clics" value={s.totals.clicks} cur={s.totals.clicks} prev={s.prev_totals.clicks} testId="gsc-kpi-clicks" />
            <Kpi label="Impressions" value={s.totals.impressions} cur={s.totals.impressions} prev={s.prev_totals.impressions} testId="gsc-kpi-impressions" />
            <Kpi label="CTR moyen" value={`${s.totals.ctr} %`} cur={s.totals.ctr} prev={s.prev_totals.ctr} testId="gsc-kpi-ctr" />
            <Kpi label="Position moyenne" value={s.totals.position} cur={s.totals.position} prev={s.prev_totals.position} invert testId="gsc-kpi-position" />
          </div>
          <Spark daily={s.daily} />
          <p className="text-[11px] text-gray-400 mt-1 mb-3">Période {s.period.start} → {s.period.end}, comparée aux {s.days} jours précédents · récupéré le {new Date(s.fetched_at).toLocaleString('fr-FR')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Table rows={s.pages} keyName="page" label="Pages les plus cliquées" testId="gsc-pages" />
            <Table rows={s.queries} keyName="query" label="Requêtes Google" testId="gsc-queries" />
          </div>
        </>
      )}
    </div>
  );
};

export default SeoPerformancePanel;
